import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldType } from './field.types';
import { Autocomplete, TextField } from '@mui/material';
import { checkIsValidFieldValue } from '../util/helper';
import { useFormValidation } from '../context/FormValidation';
import { useTranslation } from 'react-i18next';
import { useSingleForm } from '../context/SingleForm';

type SingleLineProps = FieldType & {
  placeholder?: string;
  valuePattern: string;
  valuePatternHumanReadable?: string;
  maxLength: number;
  values?: string[] | undefined;
};

export const SingleLineField: React.FC<SingleLineProps> = ({
  values,
  fieldKey,
  name,
  required,
  placeholder,
  defaultValue,
  description,
  maxLength,
  valuePattern,
  valuePatternHumanReadable,
  onChangeFieldValue,
}) => {
  const [value, setValue] = useState<string>(defaultValue || '');
  const { t } = useTranslation();
  const [isValid, setIsValid] = useState<boolean>(true);
  const { showError, updateFieldValidation } = useFormValidation();
  const { formIndex } = useSingleForm();

  const setFieldValidity = useCallback(
    (newIsValid: boolean) => {
      setIsValid(newIsValid);
      updateFieldValidation(`${formIndex}-${fieldKey}`, newIsValid);
    },
    [updateFieldValidation, formIndex, fieldKey]
  );

  useEffect(() => {
    const isValid = checkIsValidFieldValue(required, valuePattern, defaultValue);
    setFieldValidity(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isError = useMemo(() => {
    return !isValid && showError;
  }, [isValid, showError]);

  const onChange = useCallback(
    (event: any, value?: any) => {
      // on pressing the clear button
      if (event['_reactName'] === 'onClick' && value === null) {
        setFieldValidity(!required);
        setValue('');
        onChangeFieldValue(fieldKey, '');
        return;
      }

      // set correct value when selecting instead of typing
      let newValue: string = event.target.value;
      if (event.target.value === 0 && value !== null) {
        newValue = value;
      }

      const isValid = checkIsValidFieldValue(required, valuePattern, newValue);
      setFieldValidity(isValid);
      setValue(newValue);
      onChangeFieldValue(fieldKey, newValue);
    },
    [required, setFieldValidity, onChangeFieldValue, fieldKey, valuePattern]
  );

  const showPatternMessage: boolean = !isValid && value.length > 0 && !!valuePattern;
  const helperText = showPatternMessage
    ? t('forms.common.incorrect-field-pattern', { pattern: valuePatternHumanReadable || valuePattern })
    : description;

  if (values !== undefined && values.length > 0) {
    return (
      <Autocomplete
        freeSolo
        options={values}
        defaultValue={defaultValue}
        onChange={onChange}
        renderInput={params => {
          const mergedInputProps = { ...params.inputProps, maxLength: maxLength, pattern: valuePattern };
          return (
            <TextField
              {...params}
              id={`${formIndex}-${fieldKey}`}
              name={fieldKey}
              required={required}
              label={name}
              placeholder={placeholder}
              helperText={helperText}
              inputProps={mergedInputProps}
              multiline={false}
              onChange={onChange}
              onSelect={onChange}
              error={isError}
            />
          );
        }}
      />
    );
  }
  return (
    <TextField
      id={`${formIndex}-${fieldKey}`}
      required={required}
      name={fieldKey}
      label={name}
      placeholder={placeholder}
      defaultValue={defaultValue}
      helperText={helperText}
      inputProps={{ maxLength: maxLength, pattern: valuePattern }}
      onChange={onChange}
      error={isError}
      variant="outlined"
    />
  );
};
